import React, { useState } from "react";
import { useGetMembersQuery } from "../api/membershipApi";
import { BASE_URL } from "../../../features/api/apiSlice";
import { useAuth } from "../../../context/AuthContexts";
import {
  useGetStatesQuery,
  useGetChaptersQuery,
  useGetSectorsQuery,
  useGetLgasQuery,
  useGetActiveUserCategoriesQuery,
} from "../../settings/api/settingsApi";

import { formatCurrency, formatDate } from "../../../utils/formatters";
import DataTable from "../components/DataTable";
import TableSkeleton from "../components/TableSkeleton";
import useDebounce from "../components/useDebounce";

export default function MembershipManagement() {
  const { hasPermission, hasRole } = useAuth();

  // hasPermission is a plain list check with no super-admin bypass, so a super
  // admin who has not been granted the row would lose the export entirely.
  const canExport = hasPermission("member.export") || hasRole("super_admin");

  // Logic states
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("created_at");
  const [dir, setDir] = useState("desc");

  // Filter states
  const [categoryId, setCategoryId] = useState("");
  const [stateId, setStateId] = useState("");
  const [lgaId, setLgaId] = useState("");
  const [chapterId, setChapterId] = useState("");
  const [sectorId, setSectorId] = useState("");
  // Date of registration, as a range. Either end stands on its own.
  const [regFrom, setRegFrom] = useState("");
  const [regTo, setRegTo] = useState("");
  // "" shows both, as the register always has. Only "living"/"deceased" narrow.
  const [deceased, setDeceased] = useState("");
  // Financial standing: "" both, "up_to_date" / "owing" narrow. The server
  // answers this from the same invoices the balance column is summed from.
  const [financial, setFinancial] = useState("");
  // Whether the member has a real address at all — the importers minted
  // placeholders wherever none existed, and mailing one is a hard bounce.
  const [emailStatus, setEmailStatus] = useState("");
  // Registration dates the register cannot mean — see isInvalidRegDate below.
  const [regDateStatus, setRegDateStatus] = useState("");
  // Whether the member has confirmed their portal account. A different
  // question from emailStatus above: that one is about whether the ADDRESS is
  // real, this is about whether they ever clicked the link.
  const [verification, setVerification] = useState("");

  // Ten controls stacked above the register would push it off a phone
  // entirely, so they fold behind a button there. Always open from `lg` up.
  const [showFilters, setShowFilters] = useState(false);
  const activeFilterCount = [
    categoryId, stateId, lgaId, chapterId, sectorId, regFrom, regTo, deceased,
    financial, emailStatus, regDateStatus, verification,
  ].filter(Boolean).length;

  const debouncedSearch = useDebounce(search);

  // Data Hooks
  const { data: members, isLoading: membersLoading } = useGetMembersQuery({
    page,
    per_page: perPage,
    search: debouncedSearch,
    sort_by: sort,
    sort_dir: dir,
    category_id: categoryId,
    state_id: stateId,
    lga_id: lgaId,
    chapter_id: chapterId,
    sector_id: sectorId,
    reg_from: regFrom,
    reg_to: regTo,
    deceased,
    financial,
    email_status: emailStatus,
    reg_date_status: regDateStatus,
    verification,
  });
  const { data: categories } = useGetActiveUserCategoriesQuery();
  const { data: states } = useGetStatesQuery();
  const { data: chapters } = useGetChaptersQuery();
  const { data: sectors } = useGetSectorsQuery();
  const { data: lgas } = useGetLgasQuery(stateId, { skip: !stateId }); // Only fetch LGAs if State is selected

  const [selected, setSelected] = useState([]);

  const [exportingEmails, setExportingEmails] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  /**
   * Emails for every member the current filters describe — the whole set, not
   * the page on screen. The server applies the same filters the list was built
   * from, so what downloads is what is being looked at.
   */
  const exportEmails = async () => {
    setExportingEmails(true);
    setExportError(null);
    try {
      const params = new URLSearchParams();
      const filters: Record<string, string> = {
        search: debouncedSearch,
        category_id: categoryId,
        state_id: stateId,
        lga_id: lgaId,
        chapter_id: chapterId,
        sector_id: sectorId,
        reg_from: regFrom,
        reg_to: regTo,
        deceased,
        financial,
        email_status: emailStatus,
        reg_date_status: regDateStatus,
        verification,
        sort_by: sort,
        sort_dir: dir,
      };
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params.set(k, v);
      });

      const token = localStorage.getItem("token");
      const res = await fetch(
        `${BASE_URL}/admin/membership/members/export-emails?${params.toString()}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );

      if (!res.ok) throw new Error(`Server returned ${res.status}`);

      // This API can emit stray output ahead of a response body, which would
      // land in the file. A JSON body means an error reached us as a download;
      // anything else, skip the leading whitespace and keep the BOM.
      const bytes = new Uint8Array(await res.arrayBuffer());
      const probe = new TextDecoder("latin1").decode(bytes.subarray(0, 4096));

      if (probe.trimStart().startsWith("{")) {
        throw new Error(
          new TextDecoder().decode(bytes.subarray(0, 300)).trim() ||
            "The server did not return a spreadsheet.",
        );
      }

      const start = Math.max(0, probe.search(/\S/));
      const blob = new Blob([bytes.subarray(start)], {
        type: "text/csv;charset=utf-8;",
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `member-emails-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setExportError(String(e?.message || "Could not export the email list."));
    } finally {
      setExportingEmails(false);
    }
  };

  const isDeceased = (r: any) => !!Number(r.deceased);

  /**
   * A stand-in address rather than a real one — what the importers wrote where
   * a member had none. Matched on the substring, the same way the server and
   * `members:placeholder-emails` match, so all three minted patterns count.
   */
  const isPlaceholderEmail = (r: any) =>
    [r.email, r.user?.email].some((e) =>
      String(e ?? "").toLowerCase().includes("placeholder"),
    );

  /**
   * A registration date the register cannot mean — the same four tests the
   * server filters on (MemberAdminController::invalidRegDate): before the
   * Institute existed, the unix epoch, in the future, or on/before the member's
   * own date of birth. A missing date is not "invalid" here; it is its own
   * filter and its own fix.
   *
   * ISO dates compare correctly as strings, so this needs no date parsing —
   * which also keeps a `0000-00-00` from becoming an Invalid Date nobody can
   * reason about.
   */
  const isInvalidRegDate = (r: any) => {
    const reg = String(r.reg_date ?? "").slice(0, 10);
    if (!reg) return false;

    const dob = String(r.dob ?? "").slice(0, 10);
    const today = new Date().toISOString().slice(0, 10);

    return (
      reg < "1969-01-01" ||
      reg === "1970-01-01" ||
      reg > today ||
      (!!dob && reg <= dob)
    );
  };

  const columns = [
    {
      key: "name",
      header: "Full Name",
      accessor: (r: any) => (
        <span className="flex flex-wrap items-center gap-1.5">
          <span>{`${r.surname} ${r.firstname} ${r.othernames ?? ""}`}</span>
          {/* The tinted row says it at a glance; this says it to anyone reading
              a printout, a screen reader, or an exported page where the colour
              does not survive. */}
          {isDeceased(r) && (
            <span className="inline-flex text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded bg-gray-200 text-gray-600">
              Deceased
            </span>
          )}
        </span>
      ),
    },
    { key: "membership_no", header: "Membership No" },
    {
      // The date they joined the Institute — NOT created_at, which for an
      // imported member is the day of the import rather than anything about
      // their membership.
      key: "reg_date",
      header: "Date Registered",
      // A date that cannot be right is shown AS the register holds it, flagged
      // — hiding it would leave whoever has to correct it with nothing to go on.
      accessor: (r: any) =>
        !r.reg_date ? (
          <span className="text-gray-300">—</span>
        ) : isInvalidRegDate(r) ? (
          <span className="flex flex-wrap items-center gap-1.5">
            <span className="text-amber-700">{String(r.reg_date).slice(0, 10)}</span>
            <span className="inline-flex text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
              Check
            </span>
          </span>
        ) : (
          formatDate(r.reg_date)
        ),
    },
    {
      header: "Financial Status",
      key: "debt",
      sortable: false,
      accessor: (r: any) => (
        <span
          className={
            r.user?.total_debt > 0 ? "text-red-600 font-bold" : "text-green-600"
          }
        >
          {formatCurrency(r.user?.total_debt)}
        </span>
      ),
    },

    {
      key: "verification",
      header: "Verification",
      sortable: false,
      accessor: (r: any) => (
        <span
          className={
            r.user?.email_verified_at
              ? "inline-flex text-[11px] font-bold px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200"
              : "inline-flex text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200"
          }
        >
          {r.user?.email_verified_at ? "Verified" : "Unverified"}
        </span>
      ),
    },

    {
      key: "email",
      header: "Email",
      // A placeholder is printed muted and labelled: it looks exactly like an
      // address, and anyone reading the row needs to know that writing to it
      // achieves nothing.
      accessor: (r: any) =>
        isPlaceholderEmail(r) ? (
          <span className="flex flex-wrap items-center gap-1.5">
            <span className="text-gray-400 line-through">{r.email || r.user?.email}</span>
            <span className="inline-flex text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
              Placeholder
            </span>
          </span>
        ) : (
          r.email || <span className="text-gray-300">—</span>
        ),
    },
    { key: "phone", header: "Phone" },

    {
      key: "chapter",
      header: "Chapter",
      accessor: (r: any) => r.chapter?.name ?? "N/A",
    },
    {
      key: "sector",
      header: "Sector",
      accessor: (r: any) => r.sector?.name ?? "N/A",
    },
    {
      key: "category",
      header: "Category",
      accessor: (r: any) => r?.grade?.name ?? "N/A",
    },
    {
      key: "state",
      header: "State",
      accessor: (r: any) => r?.state?.name ?? "N/A",
    },
    {
      key: "lga",
      header: "LGA",
      accessor: (r: any) => r?.lga?.name ?? "N/A",
    },
    {
      key: "action",
      header: "Action",
      accessor: (r: any) => (
        <a
          href={`/member-list/${r.id}`}
          className="px-3 py-1 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition-colors"
        >
          View
        </a>
      ),
    },
  ];

  /**
   * One member as a card, for screens the twelve-column table does not fit.
   *
   * Ordered by what someone looking a member up actually wants: who they are,
   * their number, whether they owe. Chapter, sector, state and LGA follow as a
   * single line of context — they are how the list gets filtered, not what is
   * read off a row.
   */
  const mobileCard = (r: any) => {
    const fullName = `${r.surname ?? ""} ${r.firstname ?? ""} ${r.othernames ?? ""}`.trim();
    const verified = !!r.user?.email_verified_at;
    const debt = Number(r.user?.total_debt ?? 0);
    const place = [r.chapter?.name, r.sector?.name, r.state?.name, r.lga?.name]
      .filter(Boolean).join(" · ");

    return (
      <div className="space-y-2.5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-bold text-gray-900 leading-tight break-words">
              {fullName || "—"}
            </div>
            <div className="text-[11px] font-black text-gray-400 uppercase tracking-widest mt-0.5">
              {r.membership_no || "No member no."}
            </div>
            {r.reg_date && (
              <div
                className={`text-[11px] font-medium mt-0.5 ${
                  isInvalidRegDate(r) ? "text-amber-600" : "text-gray-400"
                }`}
              >
                Registered{" "}
                {isInvalidRegDate(r)
                  ? `${String(r.reg_date).slice(0, 10)} · check`
                  : formatDate(r.reg_date)}
              </div>
            )}
          </div>
          <div className="text-right shrink-0">
            <div className={`font-bold text-sm ${debt > 0 ? "text-red-600" : "text-green-600"}`}>
              {formatCurrency(r.user?.total_debt)}
            </div>
            <div className="text-[9px] font-black text-gray-400 uppercase tracking-widest">
              {debt > 0 ? "Owing" : "Clear"}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          <span
            className={
              verified
                ? "inline-flex text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200"
                : "inline-flex text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200"
            }
          >
            {verified ? "Verified" : "Unverified"}
          </span>
          <span className="inline-flex text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200">
            {r?.grade?.name ?? "No grade"}
          </span>
          {isDeceased(r) && (
            <span className="inline-flex text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-200 text-gray-600 border border-gray-300">
              Deceased
            </span>
          )}
        </div>

        {/* Long addresses wrap rather than stretching the card sideways. */}
        <div className="space-y-0.5 text-xs text-gray-500 font-medium break-words">
          {r.email && (
            <div className={isPlaceholderEmail(r) ? "text-amber-600" : undefined}>
              {r.email}
              {isPlaceholderEmail(r) && " · placeholder"}
            </div>
          )}
          {r.phone && <div>{r.phone}</div>}
          {place && <div className="text-[11px] text-gray-400">{place}</div>}
        </div>

        <a
          href={`/member-list/${r.id}`}
          className="block text-center px-3 py-2.5 bg-blue-50 text-blue-600 text-xs font-bold rounded-lg hover:bg-blue-100 transition-colors"
        >
          View member
        </a>
      </div>
    );
  };

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 bg-white rounded-lg shadow-sm">
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-3 sm:gap-4">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Member Directory</h2>

        {canExport && (
          <button
            onClick={exportEmails}
            disabled={exportingEmails || !members?.total}
            title={
              members?.total
                ? `Download the email addresses of all ${Number(members.total).toLocaleString()} filtered member(s)`
                : "No members match the current filters"
            }
            className="w-full sm:w-auto px-4 py-2.5 sm:py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-xs font-bold rounded-lg transition-all"
          >
            {exportingEmails
              ? "Preparing…"
              : `Export emails${members?.total ? ` (${Number(members.total).toLocaleString()})` : ""}`}
          </button>
        )}
      </div>

      {exportError && (
        <div className="px-4 py-3 rounded-lg border border-red-200 bg-red-50 text-sm font-semibold text-red-700">
          {exportError}
        </div>
      )}

      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-4">
        <h3 className="font-semibold text-gray-700 hidden lg:block">Filter Members</h3>

        {/* Search stays out in the open — looking a member up by name or number
            is what this page is for. The narrowing filters fold away. */}
        <div className="flex gap-2 lg:hidden">
          <input
            placeholder="Member No / name / email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 min-w-0 border px-3 py-2.5 rounded-lg text-sm focus:border-blue-500 outline-none bg-white"
          />
          <button
            onClick={() => setShowFilters((p) => !p)}
            className="shrink-0 flex items-center gap-1.5 border px-3 py-2.5 rounded-lg text-[10px] font-black uppercase tracking-widest text-gray-600 bg-white"
          >
            Filters
            {activeFilterCount > 0 && (
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-blue-600 text-white text-[9px]">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>

        <div className={`${showFilters ? "grid" : "hidden"} lg:grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3`}>
          {/* Search */}
          <input
            placeholder=" Member No/name/email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="hidden lg:block border px-3 py-2 rounded-lg text-sm focus:border-blue-500 outline-none"
          />

          {/* Category Filter */}
          <select
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            className="border px-3 py-2.5 lg:py-2 rounded-lg text-sm bg-white"
          >
            <option value="">All Categories</option>
            {categories?.map((c: any) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>

          {/* Sector Filter */}
          <select
            value={sectorId}
            onChange={(e) => setSectorId(e.target.value)}
            className="border px-3 py-2.5 lg:py-2 rounded-lg text-sm bg-white"
          >
            <option value="">All Sectors</option>
            {sectors?.map((s: any) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>

          {/* Chapter Filter */}
          <select
            value={chapterId}
            onChange={(e) => setChapterId(e.target.value)}
            className="border px-3 py-2.5 lg:py-2 rounded-lg text-sm bg-white"
          >
            <option value="">All Chapters</option>
            {chapters?.map((ch: any) => (
              <option key={ch.id} value={ch.id}>
                {ch.name}
              </option>
            ))}
          </select>

          <select
            value={perPage}
            onChange={(e) => setPerPage(Number(e.target.value))}
            className="border px-3 py-2.5 lg:py-2 rounded-lg text-sm bg-white"
          >
            <option value={25}>25 Rows</option>
            <option value={50}>50 Rows</option>
            <option value={100}>100 Rows</option>
          </select>
        </div>

        <div className={`${showFilters ? "grid" : "hidden"} lg:grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3`}>
          {/* State Filter */}
          <select
            value={stateId}
            onChange={(e) => {
              setStateId(e.target.value);
              setLgaId(""); // Reset LGA when state changes
            }}
            className="border px-3 py-2.5 lg:py-2 rounded-lg text-sm bg-white"
          >
            <option value="">All States</option>
            {states?.map((st: any) => (
              <option key={st.id} value={st.id}>
                {st.name}
              </option>
            ))}
          </select>

          {/* LGA Filter (Dependent on State) */}
          <select
            value={lgaId}
            onChange={(e) => setLgaId(e.target.value)}
            disabled={!stateId}
            className="border px-3 py-2.5 lg:py-2 rounded-lg text-sm bg-white disabled:bg-gray-100"
          >
            <option value="">All LGAs</option>
            {lgas?.map((l: any) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>

          {/* Date of registration. Labelled, because two bare date boxes do not
              say what date they are about — and either end works alone. */}
          <label className="flex items-center gap-2 border px-3 py-2 rounded-lg text-sm bg-white">
            <span className="shrink-0 text-[10px] font-black text-gray-400 uppercase tracking-widest">
              Reg. from
            </span>
            <input
              type="date"
              value={regFrom}
              onChange={(e) => {
                setRegFrom(e.target.value);
                setPage(1);
              }}
              className="w-full min-w-0 outline-none text-sm"
            />
          </label>

          <label className="flex items-center gap-2 border px-3 py-2 rounded-lg text-sm bg-white">
            <span className="shrink-0 text-[10px] font-black text-gray-400 uppercase tracking-widest">
              Reg. to
            </span>
            <input
              type="date"
              value={regTo}
              onChange={(e) => {
                setRegTo(e.target.value);
                setPage(1);
              }}
              className="w-full min-w-0 outline-none text-sm"
            />
          </label>

          <select
            value={deceased}
            onChange={(e) => {
              setDeceased(e.target.value);
              setPage(1);
            }}
            className="border px-3 py-2.5 lg:py-2 rounded-lg text-sm bg-white"
          >
            <option value="">All (living & deceased)</option>
            <option value="living">Living only</option>
            <option value="deceased">Deceased only</option>
          </select>

          {/* Financial standing — the same unsettled dues the balance column on
              each row is summed from, so a member cannot be listed here as up to
              date and shown owing in the next column. */}
          <select
            value={financial}
            onChange={(e) => {
              setFinancial(e.target.value);
              setPage(1);
            }}
            className="border px-3 py-2.5 lg:py-2 rounded-lg text-sm bg-white"
          >
            <option value="">All financial standings</option>
            <option value="up_to_date">Financially up to date</option>
            <option value="owing">Owing dues</option>
          </select>

          {/* Address quality. "Placeholder" is the stand-in the importers minted
              where a member had no email — the set worth chasing before any
              mailing, since every one of them bounces. */}
          <select
            value={emailStatus}
            onChange={(e) => {
              setEmailStatus(e.target.value);
              setPage(1);
            }}
            className="border px-3 py-2.5 lg:py-2 rounded-lg text-sm bg-white"
          >
            <option value="">All email addresses</option>
            <option value="real">Real address</option>
            <option value="placeholder">Placeholder address</option>
            <option value="missing">No address</option>
          </select>

          {/* Account verification — whether they confirmed their email, not
              whether the address is any good. The two values cover the whole
              register between them, so "Unverified" includes members who never
              signed up at all, who are most of it. */}
          <select
            value={verification}
            onChange={(e) => {
              setVerification(e.target.value);
              setPage(1);
            }}
            className="border px-3 py-2.5 lg:py-2 rounded-lg text-sm bg-white"
          >
            <option value="">All accounts</option>
            <option value="verified">Verified</option>
            <option value="unverified">Unverified</option>
          </select>

          {/* Registration dates that cannot be right — dates from before the
              Institute existed, the unix epoch, dates in the future, and dates
              on or before the member's own birth. "Missing" is kept separate
              because filling a gap and correcting a wrong date are different
              jobs. */}
          <select
            value={regDateStatus}
            onChange={(e) => {
              setRegDateStatus(e.target.value);
              setPage(1);
            }}
            className="border px-3 py-2.5 lg:py-2 rounded-lg text-sm bg-white"
          >
            <option value="">All registration dates</option>
            <option value="invalid">Invalid reg. date</option>
            <option value="missing">Missing reg. date</option>
            <option value="invalid_or_missing">Invalid or missing reg. date</option>
          </select>

          <button
            onClick={() => {
              setSearch("");
              setCategoryId("");
              setStateId("");
              setLgaId("");
              setChapterId("");
              setSectorId("");
              setRegFrom("");
              setRegTo("");
              setDeceased("");
              setFinancial("");
              setEmailStatus("");
              setRegDateStatus("");
              setVerification("");
              setPage(1);
            }}
            className="text-red-600 text-sm font-medium hover:bg-red-50 py-2.5 lg:py-2 px-4 rounded-lg transition-colors border lg:border-0 border-red-100 sm:col-span-2 lg:col-span-1"
          >
            Clear All Filters
          </button>
        </div>
      </div>

      {membersLoading ? (
        <TableSkeleton />
      ) : (
        <DataTable
          columns={columns}
          data={members?.data || []}
          pagination={members}
          onPageChange={setPage}
          sort={sort}
          setSort={setSort}
          dir={dir}
          setDir={setDir}
          selected={selected}
          setSelected={setSelected}
          canExport={canExport}
          mobileCard={mobileCard}
          rowClass={(r: any) =>
            isDeceased(r) ? "bg-gray-200/70 text-gray-500 hover:bg-gray-300/70" : ""
          }
        />
      )}
    </div>
  );
}
